import { FC, forwardRef, useMemo } from 'react';
import { getRedditUserLeveIcon, getRedditUserLevelName, getUserInitials } from 'wb-reddit/app/routes/users/utils';
import RedditAccessImages from 'wb-reddit/app/routes/reports/components/accessImages';
import {
	Button,
	ButtonAppearance, ButtonColor,
	Counter,
	Tooltip,
	TrashCanIcon,
	useOpen,
	isReddit,
	isSpotify,
	AvatarEntity,
} from '@aleph/shared-components';
import {
	RedditUsersListQueryItems,
	UserAccountLevel,
} from 'wb-reddit/graphql/generated';
import { User } from 'wb-spotify/graphql/authgraph/generated';
import useRedditUserMutationStorage from 'wb-reddit/app/routes/users/mutator/useRedditUserMutationStorage';
import { MutatingRedditUser } from 'wb-reddit/app/routes/users/mutator/utils';
import { getEntityTypeName } from 'wb-reddit/app/common/utils';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import DeleteDialog from 'wb-reddit/app/routes/users/components/deleteDialog';
import EntityName from 'wb-reddit/app/common/components/EntityName';
import { Link } from 'react-router-dom';
import { HandleUpdateUserRow, UserRow } from 'wb-reddit/app/routes/users/container';
import { getUserAdAccountAccess } from 'wb-spotify/app/common/utils';
import useSpotifyRolePermissions from 'wb-spotify/app/common/hooks/useSpotifyPermissions';

interface IProps {
	deleteRow: HandleUpdateUserRow
	user: UserRow
}

const RedditUserListItem: FC<IProps> = forwardRef<
HTMLDivElement, IProps
>(({ user, deleteRow, ...rest }, ref) => {
	const { setMutationUser } = useRedditUserMutationStorage();
	const [deleteOpen, { onOpen: openDelete, onClose: closeDelete }] = useOpen(false);

	const platformUser = useUserMetaData();
	const userAccess = useMemo(() => (isReddit()
		? ((user as RedditUsersListQueryItems).userAccess ?? [])
		: getUserAdAccountAccess(user as unknown as User)), [user]);

	function onEditUser() {
		setMutationUser({
			...user,
			userAccountLevel:
				user?.userAccountLevel !== UserAccountLevel.Unknown ? user.userAccountLevel : undefined,
		} as MutatingRedditUser);
	}

	const { canDeleteUser: spotifyCanDeleteUser, canEditUser: spotifyCanEditUser } = useSpotifyRolePermissions();

	const canDeleteUser = useMemo(() => {
		if (isReddit()) {
			return platformUser?.isAdmin;
		}

		return spotifyCanDeleteUser;
	}, [platformUser, spotifyCanDeleteUser]);

	const canEditUser = useMemo(() => {
		if (isReddit()) {
			return platformUser?.isAdmin;
		}

		return spotifyCanEditUser;
	}, [platformUser, spotifyCanEditUser]);

	return (
		<div
			className="flex w-full h-[82px] border-b border-b-200 items-center"
			{...rest}
			ref={ref}
		>
			<DeleteDialog
				user={user}
				isOpen={deleteOpen}
				onClose={closeDelete}
				deleteRow={deleteRow}
			/>

			<EntityName
				name={`${user.userName} ${user.userSurname || ''}`}
				onClick={canEditUser ? onEditUser : undefined}
				classname="whitespace-nowrap min-w-[250px]"
				avatarSource={user.userProfilePicture ?? undefined}
				entityType={AvatarEntity.User}
				entityUserInitials={getUserInitials(user)}
			/>

			<div className="w-[250px] px-[8px] whitespace-nowrap overflow-hidden overflow-ellipsis text-gray-950">
				{user.userEmail}
			</div>

			<div className="w-[215px] flex gap-[16px] items-center px-[8px] text-gray-600">
				{getRedditUserLeveIcon(user.userAccountLevel)}
				{getRedditUserLevelName(user.userAccountLevel)}
			</div>

			<div className="w-[165px] whitespace-nowrap overflow-hidden overflow-ellipsis px-[8px]">
				<Tooltip
					refElementClassNames='!flex flex-row items-center'
					content={
						userAccess.length > 0
							? userAccess?.map((access, idx) => (
								<div
									key={idx}
									className="flex gap-[8px] items-center whitespace-nowrap overflow-hidden"
								>
									<div className="overflow-hidden overflow-ellipsis">
										{access?.userAccessEntityName || access}
									</div>
									<span className="text-gray-500 text-[10px]">
										{getEntityTypeName(access?.userAccessAccessType)}
									</span>
								</div>
							)) : 'No access'

					}
					className="z-50"
				>
					<div className='flex flex-row items-center'>
						<Counter
							className="!relative"
							count={userAccess.length}
							showZero
						/>
						<p className='ml-[4px] text-[12px]'>
							common
						</p>
					</div>
					{' ∙ '}
					<span className='flex gap-[12px] text-[12px] items-center justify-center'>
						{/* TODO when spotify ad account level is done, provide the same functionality */}
						{((user as RedditUsersListQueryItems).userAccess ?? [])?.length ? <Link
							to={`/superGrid/(filters:(userId:((value:(${user.id})))))`}
							className={'cursor-pointer text-blue'}
						>Grid view</Link> : <span
							className='text-gray-600'
						>{'Grid view'}</span>
						}
					</span>
				</Tooltip>
			</div>

			<div className="w-[170px] px-[12px]">
				<RedditAccessImages
					accessEntities={user.userGroups ?? []}
				/>
			</div>

			<div className="w-[70px] flex justify-center items-center px-[12px]">
				<div>
					{
						canDeleteUser && !isSpotify()
						&& <Button
							onClick={openDelete}
							leftNode={<TrashCanIcon />}
							appearance={ButtonAppearance.GHOST}
							color={ButtonColor.RED}
						/>
					}
				</div>
			</div>
		</div>
	);
});

export default RedditUserListItem;
